/* eslint-disable no-var */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoIdToken,
} from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import appConfig from '../config/appConfig';

var poolData = {
  UserPoolId: appConfig.cognito.USER_POOL_ID,
  ClientId: appConfig.cognito.APP_CLIENT_ID,
};
var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

export async function signIn(username, password) {
  return Auth.signIn(username, password);
}

export async function changePasswordFirstLogin(newPassword, cognitoUser, userAttributes) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export async function changePassword(username, prePassword, newPassword) {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: prePassword,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        cognitoUser.changePassword(prePassword, newPassword, (err) => {
          if (err) {
            reject(err);
          } else {
            resolve(null);
          }
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export async function forgotPassword(email) {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export async function resetPassword(verificationCode, newPassword, email) {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export async function logOut() {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  const cognitoUser = userPool.getCurrentUser();

  if (cognitoUser != null) {
    cognitoUser.signOut();
  }
}

export async function refreshCognitoToken(token, onSuccess, onError) {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });
  const refreshToken = new CognitoRefreshToken({
    RefreshToken: token,
  });
  const cognitoUser = new CognitoUser({
    Username: refreshToken,
    Pool: userPool,
  });
  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(refreshToken, async (err, result) => {
      if (err) {
        reject(onError());
      } else {
        const response = result;
        resolve(await onSuccess(response));
      }
    });
  });
}

export function isTokenExpired(token) {
  const idToken = new CognitoIdToken({
    IdToken: token,
  });
  const payload = idToken.decodePayload();
  const currentTime = Date.now();
  if (currentTime >= payload.exp * 1000) {
    return true;
  }
  return false;
}

// eslint-disable-next-line arrow-body-style
export const getCurrentSession = () => {
  return new Promise((success, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('Could not retrieve current user');
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        // eslint-disable-next-line prefer-promise-reject-errors
        // eslint-disable-next-line prefer-promise-reject-errors
        reject();
        return;
      }

      if (session.isValid()) {
        success(session);
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject('Session is not valid');
      }
    });
  });
};

export const signUp = (email, password) => {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, null, null, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
};

export const getCurrentUser = () => {
  // eslint-disable-next-line no-shadow
  const userPool = new CognitoUserPool({
    UserPoolId: appConfig.cognito.USER_POOL_ID,
    ClientId: appConfig.cognito.APP_CLIENT_ID,
  });

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    throw new Error('No authenticated User');
  }

  return cognitoUser;
};

export const getJwtTokenInStorage = () => {
  try {
    const user = getCurrentUser();
    const key = `CognitoIdentityServiceProvider.${poolData.ClientId}.${user.getUsername()}.idToken`;
    const token = localStorage.getItem(key);
    return token;
  } catch (error) {
    return null;
  }
};

export const getJwtToken = async () => {
  const current = await getCurrentSession();
  const accessToken = current.getAccessToken();
  return accessToken.getJwtToken();
};

export async function isSessionValid() {
  const currentUser = await Auth.currentUserInfo();

  if (currentUser) {
    const currentSession = await Auth.currentSession();
    return currentSession.isValid();
  }
  return false;
}

export async function getSessionGroups() {
  let groups = [];
  const currentUserSession = await Auth.currentAuthenticatedUser();
  groups = currentUserSession.signInUserSession.accessToken.payload['cognito:groups'];
  return groups;
}

// eslint-disable-next-line arrow-body-style
export const changeEmail = async (previousEmail, newEmail) => {
  // eslint-disable-next-line no-return-await
  return await new Promise((resolve, reject) => {
    const currentUser = getCurrentUser();
    currentUser.getSession(async (err) => {
      if (err) {
        reject(err);
        return;
      }

      currentUser.updateAttributes(
        [
          {
            Name: 'email',
            Value: newEmail,
          },
        ],
        (err01, result) => {
          if (err01) {
            reject(err01);
            return;
          }
          resolve(result);
        },
      );
    });
  });
};
